import {
  Box,
  Button,
  Chip,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableSortLabel,
  Typography,
} from "@mui/material";
import { ReactNode } from "react";
import CustomScrollbars from "components/CustomScrollbars";
import {
  resolveSftpAuthMode,
  SFTPConnection,
  SFTPConnectionSortBy,
} from "types/interfaces/sftp";

interface SftpConnectionsTableProps {
  connections: SFTPConnection[];
  isLoading: boolean;
  isFetching?: boolean;
  selectedId?: number | null;
  onManage: (conn: SFTPConnection) => void;
  pagination?: ReactNode;
  sortBy: SFTPConnectionSortBy | undefined;
  sortOrder: "asc" | "desc";
  onSortChange: (field: SFTPConnectionSortBy) => void;
  hasUnsavedChangesForConnection?: (connectionId: number) => boolean;
}

/** Fits Active + Read/Write + Password + Key + Unsaved on one row without wrap. */
const LABELS_COLUMN_MIN_WIDTH = 480;

function SkeletonRows() {
  return (
    <>
      {Array.from({ length: 8 }).map((_, i) => (
        <TableRow key={i}>
          {Array.from({ length: 6 }).map((_, j) => (
            <TableCell key={j}>
              <Skeleton
                variant="text"
                width={j === 2 ? Math.min(LABELS_COLUMN_MIN_WIDTH - 24, 420) : j === 0 ? 160 : "80%"}
              />
            </TableCell>
          ))}
        </TableRow>
      ))}
    </>
  );
}

/** Match UserManagementTable role chips: `size="small"` + fontWeight 600, no reduced fontSize/height. */
const chipPasswordSx = {
  bgcolor: "rgba(25,118,210,0.08)",
  color: "info.dark",
  fontWeight: 600,
} as const;

const chipKeySx = {
  bgcolor: "#f3e5f5",
  color: "#6a1b9a",
  fontWeight: 600,
} as const;

const chipUnsavedSx = {
  fontWeight: 600,
  bgcolor: "#fce4ec",
  color: "#ad1457",
} as const;

function ConnectionLabels({
  conn,
  showUnsaved,
}: {
  conn: SFTPConnection;
  showUnsaved?: boolean;
}) {
  const mode = resolveSftpAuthMode(conn);
  const roleNorm = (conn.role || "read").toLowerCase();
  const isWrite = roleNorm === "write";
  return (
    <Box
      display="flex"
      gap={0.5}
      flexWrap="nowrap"
      alignItems="center"
      sx={{ minWidth: 0 }}
    >
      {conn.is_active ? (
        <Chip
          label="Active"
          size="small"
          sx={{
            bgcolor: "rgba(46,125,50,0.10)",
            color: "success.dark",
            fontWeight: 600,
          }}
        />
      ) : (
        <Chip
          label="Inactive"
          size="small"
          sx={{
            bgcolor: "rgba(237,108,2,0.10)",
            color: "warning.dark",
            fontWeight: 600,
          }}
        />
      )}
      <Chip
        label={isWrite ? "Write" : "Read"}
        size="small"
        sx={{
          fontWeight: 600,
          ...(isWrite
            ? {
                bgcolor: "rgba(237,108,2,0.12)",
                color: "warning.dark",
              }
            : {
                bgcolor: "rgba(96,125,139,0.12)",
                color: "text.secondary",
              }),
        }}
      />
      {(mode === "password" || mode === "both") && (
        <Chip label="Password" size="small" sx={chipPasswordSx} />
      )}
      {(mode === "key" || mode === "both") && (
        <Chip label="Key" size="small" sx={chipKeySx} />
      )}
      <Chip
        label="Unsaved"
        size="small"
        tabIndex={showUnsaved ? undefined : -1}
        sx={{
          ...chipUnsavedSx,
          visibility: showUnsaved ? "visible" : "hidden",
        }}
        aria-hidden={!showUnsaved}
      />
    </Box>
  );
}

export default function SftpConnectionsTable({
  connections,
  isLoading,
  isFetching = false,
  selectedId,
  onManage,
  pagination,
  sortBy,
  sortOrder,
  onSortChange,
  hasUnsavedChangesForConnection,
}: SftpConnectionsTableProps) {
  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
      <CustomScrollbars sx={{ flex: 1, minHeight: 0 }} trackOffsetTop={40}>
        <Table
          stickyHeader
          size="small"
          sx={{ minWidth: 760 + LABELS_COLUMN_MIN_WIDTH - 260 }}
        >
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 700 }}>
                <TableSortLabel
                  active={sortBy === "name"}
                  direction={sortBy === "name" ? sortOrder : "asc"}
                  onClick={() => onSortChange("name")}
                >
                  Name
                </TableSortLabel>
              </TableCell>
              <TableCell sx={{ fontWeight: 700 }}>
                <TableSortLabel
                  active={sortBy === "client_name"}
                  direction={sortBy === "client_name" ? sortOrder : "asc"}
                  onClick={() => onSortChange("client_name")}
                >
                  Client
                </TableSortLabel>
              </TableCell>
              <TableCell sx={{ fontWeight: 700, minWidth: LABELS_COLUMN_MIN_WIDTH }}>
                Labels
              </TableCell>
              <TableCell sx={{ fontWeight: 700 }}>
                <TableSortLabel
                  active={sortBy === "bucket_folder"}
                  direction={sortBy === "bucket_folder" ? sortOrder : "asc"}
                  onClick={() => onSortChange("bucket_folder")}
                >
                  Bucket / Folder
                </TableSortLabel>
              </TableCell>
              <TableCell sx={{ fontWeight: 700 }}>
                <TableSortLabel
                  active={sortBy === "created_at"}
                  direction={sortBy === "created_at" ? sortOrder : "asc"}
                  onClick={() => onSortChange("created_at")}
                >
                  Created
                </TableSortLabel>
              </TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading ? (
              <SkeletonRows />
            ) : connections.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 6 }}>
                  <Typography color="text.secondary">No SFTP connections found</Typography>
                </TableCell>
              </TableRow>
            ) : (
              connections.map((conn) => {
                const hasUnsaved =
                  typeof hasUnsavedChangesForConnection === "function"
                    ? hasUnsavedChangesForConnection(conn.id)
                    : false;
                const nameTrim = (conn.name || "").trim();
                const displayName = nameTrim || conn.username;
                const showLogin =
                  nameTrim !== "" && nameTrim !== (conn.username || "").trim();
                return (
                <TableRow
                  key={conn.id}
                  selected={conn.id === selectedId}
                  sx={{
                    opacity: isFetching ? 0.6 : 1,
                    "&.Mui-selected": { bgcolor: "rgba(31,93,107,0.06)" },
                    "&.Mui-selected:hover": { bgcolor: "rgba(31,93,107,0.10)" },
                  }}
                >
                  <TableCell>
                    <Typography variant="body2" fontWeight={600}>
                      {displayName}
                    </Typography>
                    {showLogin && (
                      <Typography variant="caption" color="text.secondary" display="block">
                        @{conn.username}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {(conn.client_name || "").trim() ? conn.client_name : "—"}
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ minWidth: LABELS_COLUMN_MIN_WIDTH }}>
                    <ConnectionLabels conn={conn} showUnsaved={hasUnsaved} />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ wordBreak: "break-word" }}>
                      {conn.bucket}
                      {conn.folder ? `/${conn.folder}` : ""}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {new Date(conn.created_at).toLocaleDateString()}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Button
                      size="small"
                      variant={conn.id === selectedId ? "contained" : "outlined"}
                      color="primary"
                      onClick={() => onManage(conn)}
                      sx={{ textTransform: "none", fontWeight: 700 }}
                    >
                      Manage
                    </Button>
                  </TableCell>
                </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </CustomScrollbars>
      {pagination}
    </Box>
  );
}
